import { format } from "date-fns";
import { useState } from "react";
import { CurrentSellerRates, FullSellerRatesHistory } from "../../models/Rates";
import { CloseIcon } from "../../styles/icons/CloseIcon";
import { Flex, Text } from "../Sellers/styles";
import { EarlyCreditCardModalVisualization } from "./EarlyCreditCardModalVisualization";
import { SightCreditCardModalVisualization } from "./SightCreditCardModalVisualization";
import { Modal, ModalBody, ModalHeader, ModalFooter, Overlay, Button } from "./styles";
import { BankSlipModalVisualization } from "./_BankSlipModalVisualization";
import { BankSlipSection } from "./_BankSlipSection";
import { DebitSection } from "./_DebitSection";
import { EarlyCreditCardSection } from "./_EarlyCreditCardSection";
import { ObservationSection } from "./_ObservationSection";
import { PixModalVisualization } from "./_PixModalVisualization";
import { PixSection } from "./_PixSection";
import { SightCreditCardSection } from "./_SightCreditCardSection";

interface ModalProps {
  modalState: boolean;
  closeModal: () => void;
  currentSellerRates: CurrentSellerRates;
  defaultSellerRates: CurrentSellerRates;
  onSubmit?: any;
  handleSubmit?: any;
  register?: any;
  errors?: any;
  watch?: any;
  control?: any;
  colorFinder?: any;
  asHistoryVisualization?: boolean;
  fullHistoryVisualization?: FullSellerRatesHistory;
}

export function TransactionalCostsModal({
  onSubmit,
  register,
  handleSubmit,
  closeModal,
  modalState,
  currentSellerRates,
  defaultSellerRates,
  watch,
  control,
  colorFinder,
  errors,
  asHistoryVisualization = false,
  fullHistoryVisualization
}: ModalProps) {
  const [isScrollNotOnTop, setIsScrollNotOnTop] = useState(false);
  const [isScrollNotOnBottom, setIsScrollNotOnBottom] = useState(false);
  const handleScroll = (event) => {
    const { scrollTop, clientHeight } = event.target;
    if(scrollTop > 0) {
      setIsScrollNotOnBottom(false);
      setIsScrollNotOnTop(true);
      return;
    }
    if(scrollTop < clientHeight ) {
      setIsScrollNotOnTop(false);
      setIsScrollNotOnBottom(true);
      return;
    }
  }
  return (
    <Overlay isOpen={modalState}>
      <Flex
        as="form"
        width="100%"
        height="100%"
        alignItems="center"
        justifyContent="center"
        padding="5% 30%"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Modal>
          <ModalHeader hasContentUpdwards={isScrollNotOnTop}>
            {asHistoryVisualization ? (
              <>
                {fullHistoryVisualization?.createAt && (
                  <>
                    <Flex flexDir="column" gap="0">
                      <Text color="black" fontSize="1rem">
                        Alteração - {format(new Date(fullHistoryVisualization?.createAt as string), "dd/MM/yy")}
                      </Text>
                      <Text color="black" fontSize=".8rem">
                        {fullHistoryVisualization?.createBy}
                      </Text>
                    </Flex>
                    <Button
                      type="button"
                      padding="0"
                      borderRadius="2px"
                      border="1px solid #D9D9D9"
                      bgColor="white"
                      width="2rem"
                      height="2rem"
                      onClick={() => closeModal()}
                    >
                      <CloseIcon size={12} />
                    </Button>
                  </>
                )}
              </>
            ) : (
              <>
                <Text color="black" fontSize="1.1rem">
                  Revise as alterações
                </Text>
                <Button
                  type="button"
                  padding="0"
                  borderRadius="2px"
                  border="1px solid #D9D9D9"
                  bgColor="white"
                  width="2rem"
                  height="2rem"
                  onClick={() => closeModal()}
                >
                  <CloseIcon size={12} />
                </Button>
              </>
            )}
          </ModalHeader>
          <ModalBody onScroll={handleScroll}>
            {!asHistoryVisualization && (
              <>
                <ObservationSection
                  asPreview
                  asModalConfirmation
                  observation={watch("observation")}
                  register={register}
                  watch={watch}
                  errors={errors?.observation}
                />
                <PixSection
                  asModalView
                  register={register}
                  watch={watch}
                  errors={errors}
                  colorFinder={colorFinder}
                  defaultPixConfigData={defaultSellerRates?.pixConfigData}
                  pixConfigData={currentSellerRates?.pixConfigData}
                />
                <DebitSection 
                  asModalView
                  register={register}
                  watch={watch}
                  errors={errors}
                  colorFinder={colorFinder}
                  defaultDebitConfigData={defaultSellerRates?.debitCardConfigData}
                  debitConfigData={currentSellerRates?.debitCardConfigData}
                />
                <BankSlipSection
                  asModalView
                  register={register}
                  watch={watch}
                  errors={errors}
                  colorFinder={colorFinder}
                  defaultBankSlipConfigData={defaultSellerRates?.bankSlipConfigData}
                  bankSlipConfigData={currentSellerRates?.bankSlipConfigData}
                />
                <SightCreditCardSection
                  asModalView
                  register={register}
                  watch={watch}
                  errors={errors}
                  colorFinder={colorFinder}
                  defaultSightCreditCardConfigData={defaultSellerRates?.sightCreditCardConfigData}
                  sightCreditCardConfigData={currentSellerRates?.sightCreditCardConfigData}
                />
                <EarlyCreditCardSection
                  asModalView
                  register={register}
                  watch={watch}
                  control={control}
                  errors={errors}
                  colorFinder={colorFinder}
                  defaultEarlyCreditCardConfigData={defaultSellerRates?.earlyCreditCardConfigData}
                  earlyCreditCardConfigData={currentSellerRates?.earlyCreditCardConfigData}
                />
              </>
            )}
            {asHistoryVisualization && (
              <>
                <PixModalVisualization
                  actualValue={fullHistoryVisualization}
                  defaultPixConfigData={defaultSellerRates?.pixConfigData}
                  pixConfigData={currentSellerRates?.pixConfigData}
                  colorFinder={colorFinder}
                />

                <BankSlipModalVisualization
                  actualValue={fullHistoryVisualization}
                  defaultBankSlipConfigData={defaultSellerRates?.bankSlipConfigData}
                  bankSlipConfigData={currentSellerRates?.bankSlipConfigData}
                  colorFinder={colorFinder}
                />

                <SightCreditCardModalVisualization
                  actualValue={fullHistoryVisualization}
                  defaultSightCreditCardConfigData={defaultSellerRates?.sightCreditCardConfigData}
                  sightCreditCardConfigData={currentSellerRates?.sightCreditCardConfigData}
                  colorFinder={colorFinder}
                />

                <EarlyCreditCardModalVisualization
                  actualValue={fullHistoryVisualization}
                  defaultEarlyCreditCardConfigData={defaultSellerRates?.earlyCreditCardConfigData}
                  earlyCreditCardConfigData={currentSellerRates?.earlyCreditCardConfigData}
                  colorFinder={colorFinder}
                />

                <ObservationSection
                  register={register}
                  watch={watch}
                  errors={errors?.observation}
                  asPreview
                  observation={fullHistoryVisualization?.observation}
                />
              </>
            )}
          </ModalBody>
          <ModalFooter hasContentDownwards={isScrollNotOnBottom}>
            {asHistoryVisualization ? (
              <>
                <div></div>
                <Button
                  type="button"
                  borderRadius="2px"
                  onClick={closeModal}
                >
                  <Text color="white" fontSize=".8rem">
                    Ok
                  </Text>
                </Button>
              </>
            ) : (
              <>
                <Button
                  type="button"
                  padding="0"
                  bgColor="white"
                  onClick={closeModal}
                >
                  <Text color="#000000D9" fontSize=".8rem">
                    Voltar para edição
                  </Text>
                </Button>
                <Button
                  type="submit"
                  borderRadius="2px"
                >
                  <Text color="white" fontSize=".8rem">
                    Confirmar
                  </Text>
                </Button>
              </>
            )}
          </ModalFooter>
        </Modal>
      </Flex >
    </Overlay >
  )
}